<div class="bg-background min-h-screen text-on-surface">

  <!-- Hero Section -->
  <section class="relative pt-24 pb-20 overflow-hidden">
    <div class="absolute inset-0 pointer-events-none">
      <div class="absolute top-[-10%] left-1/4 w-[600px] h-[600px] rounded-full bg-primary/5 blur-[120px]"></div>
      <div class="absolute bottom-[-10%] right-1/4 w-[400px] h-[400px] rounded-full bg-secondary/5 blur-[120px]"></div>
    </div>
    <div class="max-w-7xl mx-auto px-6 relative z-10">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div class="lg:col-span-7">
          <span class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-container text-on-secondary-container text-xs font-bold uppercase tracking-wider mb-6">
            <span class="material-symbols-outlined text-[14px]">extension</span>
            Platform Modules
          </span>
          <h1 class="text-5xl md:text-6xl font-extrabold tracking-tighter text-on-background mb-6 leading-[1.1]">
            Every Module.<br>
            <span class="bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary-container">One Platform.</span>
          </h1>
          <p class="text-xl text-on-surface-variant leading-relaxed mb-10 max-w-2xl">
            ZAD ERP is built from a set of deeply integrated, enterprise-grade modules. Each one is purpose-built for precision — and all of them share a single unified data layer.
          </p>
          <div class="flex flex-col sm:flex-row gap-4">
            <a routerLink="/create-account" class="bg-gradient-to-r from-primary to-primary-container text-white px-10 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-primary/20 transition-all active:scale-95 text-center">Start Free Trial</a>
            <a routerLink="/pricing" class="bg-surface-container-high text-on-primary-fixed-variant px-10 py-4 rounded-xl font-bold text-lg hover:bg-surface-container-highest transition-all active:scale-95 text-center">View Pricing</a>
          </div>
        </div>
        
        <div class="lg:col-span-5 relative hidden lg:block">
          <div class="absolute -top-20 -right-20 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
          <div class="relative bg-surface-container-lowest rounded-2xl p-4 shadow-[0_12px_40px_rgba(25,28,30,0.06)] border border-outline-variant/15 transform lg:rotate-2 lg:hover:rotate-0 transition-transform duration-500">
            <img class="rounded-xl w-full h-auto object-cover aspect-video" alt="ZAD ERP Modules Analytics Dashboard" src="https://lh3.googleusercontent.com/aida-public/AB6AXuDkLGFOYw6035d6WVKnwUiBJf4lfJ--_RgtvOmhoBb-IBFyT5JRIN6wM_LzWI7Bh9SOUvH9EVL-SHoK9EY8ZoRcKTjkepIcEttUcPIjNQcP7myNGKtxKU1ICwSUrsSkgDr43A311pJtd8brkqvYjtXTSzrNcOPrMf06g4abQJ4Q7IOGOiLHM-1iDdQ5_DMagfoiLpDFLQCUwl289IQXXZIMlfyIUUwyH0BeKo7FGcwAYRT6NPXes_WOBly2cyavbc8yaqBZD9jS330" />
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Why Unified Section -->
  <section class="py-16 bg-surface-container-low border-y border-outline-variant/10">
    <div class="max-w-7xl mx-auto px-6">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div class="flex items-start gap-4">
          <div class="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span class="material-symbols-outlined text-primary">hub</span>
          </div>
          <div>
            <h3 class="font-bold text-on-surface mb-1">Unified Data Layer</h3>
            <p class="text-sm text-on-surface-variant leading-relaxed">All modules share a single database — no data silos, no integration headaches.</p>
          </div>
        </div>
        <div class="flex items-start gap-4">
          <div class="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
            <span class="material-symbols-outlined text-secondary">tune</span>
          </div>
          <div>
            <h3 class="font-bold text-on-surface mb-1">Modular Deployment</h3>
            <p class="text-sm text-on-surface-variant leading-relaxed">Activate only what you need. Scale with additional modules as your business grows.</p>
          </div>
        </div>
        <div class="flex items-start gap-4">
          <div class="w-12 h-12 rounded-xl bg-tertiary/10 flex items-center justify-center flex-shrink-0">
            <span class="material-symbols-outlined text-tertiary">security</span>
          </div>
          <div>
            <h3 class="font-bold text-on-surface mb-1">Enterprise-Grade Security</h3>
            <p class="text-sm text-on-surface-variant leading-relaxed">Role-based access control and audit logs ensure data integrity across every module.</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Modules Grid -->
  <section class="py-24">
    <div class="max-w-7xl mx-auto px-6">
      <div class="text-center mb-16">
        <h2 class="text-3xl md:text-4xl font-extrabold tracking-tight text-on-background mb-4">The Full Module Suite</h2>
        <p class="text-on-surface-variant max-w-xl mx-auto">Click any module to explore its capabilities.</p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        @for (module of modules; track module.id) {
          <div
            (click)="toggleModule(module.id)"
            class="group bg-surface-container-lowest rounded-[2rem] border border-outline-variant/10 overflow-hidden cursor-pointer transition-all duration-300 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5"
            [class.border-primary]="activeModule === module.id"
            [class.shadow-lg]="activeModule === module.id">

            <!-- Card Top -->
            <div class="p-8">
              <div class="flex justify-between items-start mb-6">
                <div class="w-14 h-14 rounded-2xl flex items-center justify-center"
                  [ngClass]="module.color === 'primary' ? 'bg-primary/10' : module.color === 'secondary' ? 'bg-secondary/10' : 'bg-tertiary/10'">
                  <span class="material-symbols-outlined text-3xl"
                    [ngClass]="module.color === 'primary' ? 'text-primary' : module.color === 'secondary' ? 'text-secondary' : 'text-tertiary'">{{module.icon}}</span>
                </div>
                <span class="material-symbols-outlined text-xl text-outline-variant transition-all duration-300"
                  [class.rotate-45]="activeModule === module.id"
                  [ngClass]="activeModule === module.id ? 'text-primary' : 'text-outline-variant'">add</span>
              </div>
              <h3 class="text-xl font-bold text-on-surface mb-1">{{module.name}}</h3>
              <p class="text-xs font-bold uppercase tracking-wider text-on-surface-variant/60 mb-3">{{module.tagline}}</p>
              <p class="text-on-surface-variant text-sm leading-relaxed">{{module.description}}</p>
            </div>

            <!-- Expanded Features -->
            @if (activeModule === module.id) {
              <div class="px-8 pb-8 border-t border-outline-variant/10 pt-6 bg-surface-container-low/40">
                <p class="text-xs font-bold uppercase tracking-widest text-on-surface-variant/50 mb-4">Key Capabilities</p>
                <ul class="space-y-3">
                  @for (feature of module.features; track feature) {
                    <li class="flex items-center gap-3 text-sm text-on-surface">
                      <span class="material-symbols-outlined text-primary text-[18px]">check_circle</span>
                      {{feature}}
                    </li>
                  }
                </ul>
                @if (module.link) {
                  <a [routerLink]="module.link" class="mt-6 inline-flex items-center gap-2 text-primary font-bold text-sm hover:underline">
                    Explore {{module.name}}
                    <span class="material-symbols-outlined text-[16px]">arrow_forward</span>
                  </a>
                }
              </div>
            }
          </div>
        }
      </div>
    </div>
  </section>

  <!-- Integration Banner -->
  <section class="py-20 bg-surface-container-low">
    <div class="max-w-7xl mx-auto px-6">
      <div class="bg-surface-container-lowest rounded-[2rem] p-10 md:p-16 border border-outline-variant/10 relative overflow-hidden">
        <div class="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-primary/5 blur-[80px]"></div>
        <div class="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 relative z-10">
          <div class="max-w-xl">
            <h2 class="text-2xl md:text-3xl font-extrabold tracking-tight text-on-background mb-4">All modules work together out of the box.</h2>
            <p class="text-on-surface-variant leading-relaxed">When you create an invoice in Accounting, it automatically pulls customer data from CRM and updates Inventory. That's the power of a truly unified system.</p>
          </div>
          <div class="flex flex-col gap-4 flex-shrink-0">
            <a routerLink="/create-account" class="bg-primary text-white px-8 py-4 rounded-xl font-bold text-center hover:bg-primary-container transition-all">Start Free Trial</a>
            <a routerLink="/contact" class="text-primary font-bold text-sm text-center hover:underline">Talk to an expert →</a>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- CTA -->
  <section class="py-24 bg-on-background text-white overflow-hidden relative">
    <div class="max-w-7xl mx-auto px-6 text-center relative z-10">
      <h2 class="text-4xl md:text-5xl font-black tracking-tight mb-6">Ready to build your stack?</h2>
      <p class="text-lg text-slate-400 mb-10 max-w-2xl mx-auto">Select the modules your business needs today and unlock more as you grow. ZAD ERP scales with you.</p>
      <div class="flex flex-col sm:flex-row justify-center gap-4">
        <a routerLink="/create-account" class="bg-primary text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-primary-container transition-all">Get Started</a>
        <a routerLink="/pricing" class="border border-outline-variant/30 text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-white/5 transition-all">View Pricing</a>
      </div>
    </div>
    <div class="absolute -bottom-24 -right-24 w-96 h-96 bg-primary/20 rounded-full blur-[120px]"></div>
    <div class="absolute -top-24 -left-24 w-96 h-96 bg-secondary/10 rounded-full blur-[120px]"></div>
  </section>

</div>
